/** tm/legal/LegalDetailParts.tsx — 상세 공용 조각: 머리(배지·제목·← 목록), 메타 표, 요지 상자, 접는 본문 */
import { useState, type ReactNode } from 'react';
import { C, T, R } from '../components/ui';
import { btn, MUTED } from './legalTheme';
import { TopicTag, Spinner } from './legalUi';
import LegalPipeline from './LegalPipeline';
import type { FeedTab } from './legalTypes';

export const DETAIL_MAX = 760;

/** title: 문자열 또는 «내 제목» 표시 노드 · titleTools: 제목 끝에 붙는 작은 버튼 · subTitle: 제목 아래 한 줄(원래 제목) · editor: 있으면 제목 대신 입력칸 */
export function DetailHead({ group, label, topics, title, mobile, onBack, extra, stage, titleTools, subTitle, editor }: {
  group: string | null; label: string; topics: string[]; title: ReactNode; mobile: boolean; onBack: () => void; extra?: ReactNode; stage?: FeedTab;
  titleTools?: ReactNode; subTitle?: ReactNode; editor?: ReactNode;
}) {
  return <>
    {mobile && <button type="button" className="lw-f" onClick={onBack} style={{ ...btn(), marginBottom: 10, minHeight: 40 }}>← 목록</button>}
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
      <span data-group={group || undefined} style={{ fontSize: T.sm, fontWeight: 600, padding: '2px 7px', borderRadius: R.sm, background: C.bg, color: C.sub, whiteSpace: 'nowrap' }}>{label}</span>
      {extra}
      {topics.map(t => <TopicTag key={t}>{t}</TopicTag>)}
    </div>
    {editor || <h2 style={{ fontSize: mobile ? T.lg + 1 : T.xl, fontWeight: 700, lineHeight: 1.45, margin: '0 0 10px', maxWidth: '62ch', color: C.text, wordBreak: 'keep-all' }}>{title}{titleTools}{subTitle}</h2>}
    {stage && <LegalPipeline stage={stage} />}
  </>;
}

/** 정보 줄 — 정부지원사업 상세처럼 가볍게: 회색 이름 · 값, 옅은 구분선 (이름 칸은 글자 길이만큼) */
export function MetaGrid({ rows, mobile }: { rows: [string, ReactNode][]; mobile: boolean }) {
  const cell = { padding: '7px 0', borderBottom: `1px solid ${C.bg}`, fontSize: 14 } as const;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `minmax(${mobile ? 64 : 88}px, max-content) 1fr`, columnGap: 14, marginBottom: 14, maxWidth: DETAIL_MAX }}>
      {rows.map(([k, v]) => [
        <div key={`${k}k`} style={{ ...cell, color: C.sub, fontSize: T.md }}>{k}</div>,
        <div key={`${k}v`} style={{ ...cell, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-all', color: C.text }}>{v}</div>,
      ])}
    </div>
  );
}

type BoxKind = 'main' | 'q' | 'warn';
const BOX: Record<BoxKind, { bg: string; bd: string; h: string }> = {
  main: { bg: C.accentLt, bd: '#BFDBFE', h: C.accent },
  q: { bg: C.bgAlt, bd: C.border, h: C.sub },
  warn: { bg: '#FFFBEB', bd: '#FDE68A', h: '#B45309' },
};
export function Box({ kind = 'main', title, children }: { kind?: BoxKind; title: string; children: ReactNode }) {
  const s = BOX[kind];
  return (
    <div style={{ border: `1px solid ${s.bd}`, background: s.bg, borderRadius: R.md, padding: '12px 14px', marginBottom: 12, maxWidth: DETAIL_MAX }}>
      <h4 style={{ margin: '0 0 6px', fontSize: T.md, color: s.h }}>{title}</h4>
      <div style={{ margin: 0, fontSize: 14, lineHeight: 1.8, color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

/** 나머지 본문 섹션 — 기본 접힘, 「전문 펼치기」 */
export function Collapsible({ sections }: { sections: [string, string][] }) {
  const [open, setOpen] = useState(false);
  if (!sections.length) return null;
  return (
    <div style={{ maxWidth: DETAIL_MAX, marginBottom: 12 }}>
      <button type="button" className="lw-f" aria-expanded={open} onClick={() => setOpen(o => !o)} style={{ ...btn(), padding: '5px 10px', fontSize: T.sm }}>
        {open ? '전문 접기 ▲' : `전문 펼치기 ▼`} <span style={{ color: MUTED, fontWeight: 400 }}>{sections.map(s => s[0]).join(' · ')}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}` }}>
          {sections.map(([k, v]) => (
            <section key={k} style={{ padding: '10px 2px', borderBottom: `1px solid ${C.border}` }}>
              <h4 style={{ margin: '0 0 6px', fontSize: T.sm, color: C.sub }}>{k}</h4>
              <div style={{ fontSize: 14, lineHeight: 1.8, color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 520, overflowY: 'auto' }}>{v}</div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export function DetailEmpty({ loading, error, onRetry }: { loading?: boolean; error?: string; onRetry?: () => void }) {
  return (
    <div style={{ padding: '60px 16px', textAlign: 'center', color: error ? C.red : MUTED, fontSize: T.sm, lineHeight: 1.7 }}>
      {loading ? <><Spinner /> 본문을 불러오는 중…</> : error ? <>{error}{onRetry && <div style={{ marginTop: 8 }}><button type="button" className="lw-f" onClick={onRetry} style={btn()}>다시 시도</button></div>}</>
        : '목록에서 글을 고르면 요지와 원문 링크가 여기에 나옵니다.'}
    </div>
  );
}

/** 본문을 불러오는 동안의 자리 표시 */
export function DetailSkeleton() {
  const bar = (w: string, h = 12, mb = 10) => <div className="lw-skel" style={{ width: w, height: h, borderRadius: 4, marginBottom: mb }} />;
  return (
    <div aria-busy="true" aria-label="불러오는 중" style={{ maxWidth: DETAIL_MAX }}>
      {bar('18%', 14)}{bar('70%', 20, 16)}{bar('100%', 90, 16)}{bar('100%', 120, 16)}{bar('40%', 28)}
    </div>
  );
}

export const actsRow = { display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 16px' } as const;
